import { Helmet } from 'react-helmet-async';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  twitterCard?: 'summary' | 'summary_large_image' | 'app' | 'player';
  noindex?: boolean;
  nofollow?: boolean;
  lang?: 'en' | 'fr';
}

export const SEOHead: React.FC<SEOHeadProps> = ({
  title = 'Telegram Stars Payment Platform | Accept Payments via Telegram Stars',
  description = 'Accept payments via Telegram Stars. Create payment links instantly, manage your balance, and withdraw to TON. 3% platform fee. Start accepting Telegram Stars payments today.',
  keywords = 'telegram stars payment, accept telegram stars, telegram stars payment platform, telegram stars payment gateway, telegram stars for creators, telegram stars for merchants',
  canonical,
  ogImage = 'https://www.telegram-stars.io/images/og-image.png',
  ogType = 'website',
  twitterCard = 'summary_large_image',
  noindex = false,
  nofollow = false,
  lang = 'en',
}) => {
  const siteUrl = 'https://www.telegram-stars.io';
  const fullCanonical = canonical ? `${siteUrl}${canonical}` : siteUrl;
  const fullOgImage = ogImage.startsWith('http') ? ogImage : `${siteUrl}${ogImage}`;

  const htmlLang = lang === 'fr' ? 'fr' : 'en';
  const ogLocale = lang === 'fr' ? 'fr_FR' : 'en_US';
  const languageName = lang === 'fr' ? 'French' : 'English';

  return (
    <Helmet htmlAttributes={{ lang: htmlLang }}>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="author" content="Telegram Stars Payment Platform" />
      <meta name="robots" content={`${noindex ? 'noindex' : 'index'}, ${nofollow ? 'nofollow' : 'follow'}`} />
      <meta name="language" content={languageName} />
      <meta name="revisit-after" content="7 days" />

      {/* Canonical URL */}
      <link rel="canonical" href={fullCanonical} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullCanonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullOgImage} />
      <meta property="og:site_name" content="Telegram Stars Payment Platform" />
      <meta property="og:locale" content={ogLocale} />

      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:url" content={fullCanonical} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullOgImage} />
    </Helmet>
  );
};
